import { useLocation, useNavigate } from "react-router-dom";
import { signIn } from "@/lib/auth";
import "./LoginPage.css";

/* ENSSA Reporting Portal — Mock Login (Janison Insights v2 dashboard layout).
   Ported from ~/Downloads/Janison Insights v2.html. Step 3 "View Your
   Reports" is the only active control: clicking it sets the session-scoped
   auth flag (src/lib/auth.ts) and navigates into the portal. All other CTAs
   are external (Janison/CIS) and open in a new tab. */

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const from = location.state?.from || "/";

  const handleEnter = () => {
    signIn();
    navigate(from, { replace: true });
  };

  const inert = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className="janison-login-page">
      <div className="wrap">
        <header className="janison-header" id="header">
          <div className="inner cf">
            <a href="#" className="logo-link" onClick={inert}>
              <img src="/assets/janison/logo.png" id="logo" alt="Janison Web Portal" />
            </a>
          </div>
        </header>

        <main className="main" id="main">
          <img
            src="https://insights2prodp44s3w.azureedge.net/uploads/cis/moduleassets/e921f63f-e55a-f011-bbaf-81984be7e318/e355c200-a71e-f111-b7a4-8adba3695d5b.png?v=1"
            alt=""
          />
          <h1 className="page-heading">Welcome!</h1>
          <p className="page-sub">Manage your ENSSA screening activities below.</p>

          {/* Training notice */}
          <div className="intro-flex-container">
            <div className="intro-banner">
              <strong>📋 Complete teacher training before commencing screening.</strong>
              <br />
              If you haven't done it yet, access the training portal{" "}
              <a
                href="https://www.openlearning.com/thecis/courses/early-number-sense-screener-for-australia-enssa-"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              .
            </div>
          </div>

          {/* Research activities */}
          <div className="section-block">
            <h3>Current Research Activities</h3>
            <p>
              Your involvement in our research activities helps ensure the rigour and usefulness of
              the ENSSA and its support materials. Please access the{" "}
              <strong>information letter and consent forms</strong> here:
            </p>
            <div className="consent-row">
              <a
                className="btn-consent"
                href="https://scienceoflearning.io/elementor-3230/"
                target="_blank"
                rel="noreferrer"
              >
                📄 Principals
              </a>
              <a
                className="btn-consent"
                href="https://scienceoflearning.io/elementor-3230/"
                target="_blank"
                rel="noreferrer"
              >
                📄 Teachers
              </a>
            </div>
            <div className="survey-pill">
              <div className="survey-pill-text">
                <strong>Beginning of Year Maths Survey</strong>
                <span>
                  Available until <em>August 31st</em> &nbsp;·&nbsp; ⏱ 6–10 mins
                </span>
                <br />
                <span>
                  Purpose: to gather information about school and classroom maths teaching
                  practices and investigate their association with student growth in maths.
                </span>
              </div>
              <a
                className="btn btn-info btn-info--auto"
                href="/a/delivery/candidate-dashboard"
                target="_blank"
                rel="noreferrer"
              >
                Start Survey
              </a>
            </div>
            <br />
            <div className="survey-pill">
              <div className="survey-pill-text">
                <strong>Flag students likely to need support</strong>
                <br />
                <span>
                  BEFORE SCREENING: identify children likely to need extra support in numeracy
                  here.
                </span>
              </div>
              <a
                className="btn btn-info btn-info--auto"
                href="/pages/student-information"
                target="_blank"
                rel="noreferrer"
              >
                Update Student Information
              </a>
            </div>
          </div>

          {/* Screening workflow */}
          <div className="section-block section-block--flush">
            <div className="section-block__header">
              <h3>ENSSA Screening Activities</h3>
            </div>
            <div className="workflow">
              {/* Step 1 */}
              <div className="step">
                <div className="step-header">
                  <div className="step-num-circle">1</div>
                  <h2>Generate Student Codes</h2>
                </div>
                <p>Print your student Student Codes to use during their screening session.</p>
                <a
                  className="btn btn-info"
                  href="/pages/student-codes"
                  target="_blank"
                  rel="noreferrer"
                >
                  Generate Student Codes
                </a>
              </div>

              {/* Step 2 — modal omitted; inert button */}
              <div className="step">
                <div className="step-header">
                  <div className="step-num-circle">2</div>
                  <h2>Deliver the Screener</h2>
                </div>
                <p>
                  Click below to choose your Teacher Demo Material and deliver the screener to your
                  students.
                </p>
                <button type="button" className="btn btn-info">
                  Teacher Materials
                </button>
              </div>

              {/* Step 3 — active: enters the portal */}
              <div className="step">
                <div className="step-header">
                  <div className="step-num-circle">3</div>
                  <h2>View Your Reports</h2>
                </div>
                <p>
                  Access student results and class-level insights once the screening window is
                  complete.
                </p>
                <button type="button" className="btn btn-info" onClick={handleEnter}>
                  View Reports
                </button>
              </div>
            </div>
          </div>
        </main>

        <footer className="janison-footer" id="footer">
          <div className="inner">
            <nav>
              <ul>
                <li className="help">
                  <a href="#" onClick={inert}>
                    Help
                  </a>
                </li>
              </ul>
            </nav>
            <a
              target="_blank"
              rel="noreferrer"
              href="http://www.janison.com.au/"
              title="Janison Website"
            >
              <img
                src="/assets/janison/logo-footer.png"
                alt="Site Footer Logo"
                title="Powered by Janison"
                className="footer-logo"
              />
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
